/**
 * CacheService — IndexedDB-backed cache for rendered charts, metadata
 * snapshots, and tile blobs. Uses the `idb` library for a promise-based
 * API and an LRU eviction pass keyed on `storedAt`.
 *
 * Four stores, one DB:
 *   - `charts`   : rendered PDF canvases as Blob (key = `{cycle}/{pdf_name}`)
 *   - `metadata` : arbitrary JSON blobs (key = caller-defined string)
 *   - `tiles`    : tile image / MVT blobs (key = `{dataset}/{layer}/{z}/{x}/{y}.{ext}`)
 *   - `userData` : durable user-created data such as tracks and drawings
 *
 * All writes include a `storedAt: number` (epoch ms) so `evictExpired()` can
 * prune entries older than a cutoff without a secondary index.
 */

import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'efb-cache';
const DB_VERSION = 2;

export type StoreName = 'charts' | 'metadata' | 'tiles' | 'userData';

interface CachedBlob {
  blob: Blob;
  contentType: string;
  storedAt: number;
}

interface CachedJson {
  value: unknown;
  storedAt: number;
}

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('charts'))   db.createObjectStore('charts');
      if (!db.objectStoreNames.contains('metadata')) db.createObjectStore('metadata');
      if (!db.objectStoreNames.contains('tiles'))    db.createObjectStore('tiles');
      if (!db.objectStoreNames.contains('userData')) db.createObjectStore('userData');
    },
  });
  return dbPromise;
}

// ─── Chart storage ───────────────────────────────────────────────────────────

export async function storeChart(cycle: string, pdfName: string, blob: Blob): Promise<void> {
  const db = await getDb();
  const key = `${cycle}/${pdfName}`;
  const value: CachedBlob = { blob, contentType: blob.type || 'image/png', storedAt: Date.now() };
  await db.put('charts', value, key);
}

export async function getChart(cycle: string, pdfName: string): Promise<Blob | null> {
  const db = await getDb();
  const key = `${cycle}/${pdfName}`;
  const entry = await db.get('charts', key) as CachedBlob | undefined;
  return entry?.blob ?? null;
}

export async function deleteChart(cycle: string, pdfName: string): Promise<void> {
  const db = await getDb();
  await db.delete('charts', `${cycle}/${pdfName}`);
}

// ─── Tile storage ────────────────────────────────────────────────────────────

export async function storeTile(
  dataset: string,
  layer: string,
  z: number, x: number, y: number,
  ext: string,
  blob: Blob,
): Promise<void> {
  const db = await getDb();
  const key = `${dataset}/${layer}/${z}/${x}/${y}.${ext}`;
  const value: CachedBlob = { blob, contentType: blob.type, storedAt: Date.now() };
  await db.put('tiles', value, key);
}

export async function getTile(
  dataset: string,
  layer: string,
  z: number, x: number, y: number,
  ext: string,
): Promise<CachedBlob | null> {
  const db = await getDb();
  const key = `${dataset}/${layer}/${z}/${x}/${y}.${ext}`;
  return (await db.get('tiles', key) as CachedBlob | undefined) ?? null;
}

// ─── Metadata storage ────────────────────────────────────────────────────────

export async function storeMetadata<T>(key: string, value: T): Promise<void> {
  const db = await getDb();
  const entry: CachedJson = { value, storedAt: Date.now() };
  await db.put('metadata', entry, key);
}

export async function getMetadata<T>(key: string): Promise<T | null> {
  const db = await getDb();
  const entry = await db.get('metadata', key) as CachedJson | undefined;
  return (entry?.value as T) ?? null;
}

export async function deleteMetadata(key: string): Promise<void> {
  const db = await getDb();
  await db.delete('metadata', key);
}

// ─── Durable user data ──────────────────────────────────────────────────────

export async function storeUserData<T>(key: string, value: T): Promise<void> {
  const db = await getDb();
  const entry: CachedJson = { value, storedAt: Date.now() };
  await db.put('userData', entry, key);
}

export async function getUserData<T>(key: string): Promise<T | null> {
  const db = await getDb();
  const entry = await db.get('userData', key) as CachedJson | undefined;
  return (entry?.value as T) ?? null;
}

export async function deleteUserData(key: string): Promise<void> {
  const db = await getDb();
  await db.delete('userData', key);
}

export async function listValues<T>(storeName: StoreName, prefix = ''): Promise<Array<{ key: string; value: T }>> {
  const db = await getDb();
  const out: Array<{ key: string; value: T }> = [];
  let cursor = await db.transaction(storeName, 'readonly').store.openCursor();
  while (cursor) {
    const key = String(cursor.key);
    const entry = cursor.value as CachedJson | undefined;
    if (key.startsWith(prefix) && entry?.value !== undefined) {
      out.push({ key, value: entry.value as T });
    }
    cursor = await cursor.continue();
  }
  return out;
}

// ─── Maintenance ─────────────────────────────────────────────────────────────

export async function evictExpired(storeName: StoreName, olderThanMs: number, keyPrefix = ''): Promise<number> {
  const db = await getDb();
  const cutoff = Date.now() - olderThanMs;
  const tx = db.transaction(storeName, 'readwrite');
  let deleted = 0;
  let cursor = await tx.store.openCursor();
  while (cursor) {
    const v = cursor.value as { storedAt?: number };
    if (String(cursor.key).startsWith(keyPrefix) && (v?.storedAt ?? 0) < cutoff) {
      await cursor.delete();
      deleted++;
    }
    cursor = await cursor.continue();
  }
  await tx.done;
  return deleted;
}

export async function clearStore(storeName: StoreName): Promise<void> {
  const db = await getDb();
  await db.clear(storeName);
}

export async function getStats(): Promise<Record<StoreName, number>> {
  const db = await getDb();
  return {
    charts:   await db.count('charts'),
    metadata: await db.count('metadata'),
    tiles:    await db.count('tiles'),
    userData: await db.count('userData'),
  };
}

/**
 * One-shot eviction pass using sensible defaults:
 * - charts   : 28-day cycle TTL
 * - metadata : 1 day TTL
 * - tiles    : 7 day TTL
 */
export async function evictAllExpired(): Promise<{ charts: number; metadata: number; tiles: number }> {
  const DAY = 24 * 60 * 60 * 1000;
  return {
    charts:   await evictExpired('charts',   28 * DAY),
    metadata: await evictExpired('metadata',  1 * DAY),
    tiles:    await evictExpired('tiles',     7 * DAY),
  };
}
